import { Text, createListCollection } from "@chakra-ui/react";
import * as React from "react";

import { getFacebookAdAccounts } from "../api/integration";
import { useLoading, useToaster, useWorkspaceURL } from "../hooks";
import { FacebookAdAccount } from "../types/facebook";
import { Field } from "./field";
import Link from "./link";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "./select";

interface FacebookAdAccountFieldProps {
  workspaceSlug: string;
  value: string;
  onChange: (id: string) => void;
  portalRef: React.RefObject<HTMLElement>;
  label?: string;
  required?: boolean;
}

export function FacebookAdAccountField({
  workspaceSlug,
  value,
  onChange,
  portalRef,
  label = "Facebook Ad Account",
  required = true,
}: FacebookAdAccountFieldProps) {
  const [adAccounts, setAdAccounts] = React.useState<FacebookAdAccount[]>([]);
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();

  const collection = React.useMemo(() => {
    return createListCollection({
      items: adAccounts.map((account) => ({
        value: account.id,
        label: account.name,
      })),
    });
  }, [adAccounts]);

  React.useEffect(() => {
    withLoading(async () => {
      await withToaster(async () => {
        const data = await getFacebookAdAccounts(workspaceSlug);
        setAdAccounts(data);
      });
    });
  }, [workspaceSlug, withLoading, withToaster]);

  return (
    <Field label={label} required={required}>
      {isLoading && adAccounts.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          Loading ad accounts...
        </Text>
      ) : adAccounts.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          No ad accounts found. Please connect your Facebook account in
          Settings.
        </Text>
      ) : (
        <SelectRoot
          size="sm"
          value={value ? [value] : []}
          onValueChange={({ value: v }) => onChange(v[0] ?? "")}
          collection={collection}
        >
          <SelectTrigger>
            <SelectValueText placeholder="Select an ad account" />
          </SelectTrigger>
          <SelectContent portalRef={portalRef}>
            {collection.items.map((item) => (
              <SelectItem key={item.value} item={item}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
      )}
    </Field>
  );
}

interface FacebookNotConnectedMessageProps {
  purpose: string;
}

export function FacebookNotConnectedMessage({
  purpose,
}: FacebookNotConnectedMessageProps) {
  const workspaceURL = useWorkspaceURL();
  return (
    <Text fontSize="sm" color="fg.muted">
      Please{" "}
      <Link
        to={workspaceURL("settings/integrations")}
        textDecoration="underline"
      >
        connect your Facebook account
      </Link>{" "}
      to {purpose}.
    </Text>
  );
}
